import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  LocalShipping as LocalShippingIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import AdminLayout from '../../components/layout/AdminLayout';

interface OrderItem {
  id: number;
  productName: string;
  quantity: number;
  price: number;
}

interface Order {
  id: number;
  orderNumber: string;
  customerName: string;
  customerEmail: string;
  status: 'PENDING' | 'CONFIRMED' | 'SHIPPED' | 'DELIVERED' | 'CANCELLED';
  paymentStatus: 'PENDING' | 'PAID' | 'FAILED' | 'REFUNDED';
  totalAmount: number;
  items: OrderItem[];
  shippingAddress: string;
  orderDate: string;
  deliveryDate?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const AdminOrders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  // Mock data for demonstration
  useEffect(() => {
    const mockOrders: Order[] = [
      {
        id: 1,
        orderNumber: 'ORD-2024-001',
        customerName: 'John Kim',
        customerEmail: 'kim@example.com',
        status: 'PENDING',
        paymentStatus: 'PAID',
        totalAmount: 125000,
        items: [
          { id: 1, productName: 'Premium Foam Cleanser', quantity: 2, price: 29000 },
          { id: 2, productName: 'Moisturizing Toner', quantity: 1, price: 25000 },
        ],
        shippingAddress: '123 Teheran-ro, Gangnam-gu, Seoul',
        orderDate: '2024-01-20',
      },
      {
        id: 2,
        orderNumber: 'ORD-2024-002',
        customerName: 'Jane Lee',
        customerEmail: 'lee@example.com',
        status: 'SHIPPED',
        paymentStatus: 'PAID',
        totalAmount: 87000,
        items: [
          { id: 3, productName: 'Anti-Aging Serum', quantity: 1, price: 45000 },
        ],
        shippingAddress: '456 Centum-ro, Haeundae-gu, Busan',
        orderDate: '2024-01-19',
        deliveryDate: '2024-01-22',
      },
      {
        id: 3,
        orderNumber: 'ORD-2024-003',
        customerName: 'Michael Park',
        customerEmail: 'park@example.com',
        status: 'DELIVERED',
        paymentStatus: 'PAID',
        totalAmount: 203000,
        items: [
          { id: 4, productName: 'Premium Foam Cleanser', quantity: 3, price: 29000 },
          { id: 5, productName: 'Moisturizing Toner', quantity: 2, price: 25000 },
          { id: 6, productName: 'Anti-Aging Serum', quantity: 2, price: 45000 },
        ],
        shippingAddress: '789 Dongseong-ro, Jung-gu, Daegu',
        orderDate: '2024-01-18',
        deliveryDate: '2024-01-21',
      },
    ];

    setTimeout(() => {
      setOrders(mockOrders);
      setLoading(false);
    }, 1000);
  }, []);

  const statusOptions = [
    { value: '', label: 'All' },
    { value: 'PENDING', label: 'Pending' },
    { value: 'CONFIRMED', label: 'Confirmed' },
    { value: 'SHIPPED', label: 'Shipped' },
    { value: 'DELIVERED', label: 'Delivered' },
    { value: 'CANCELLED', label: 'Cancelled' },
  ];

  const getOrdersByStatus = (status?: string) => {
    return orders.filter(order => !status || order.status === status);
  };

  const filteredOrders = getOrdersByStatus(statusFilter).filter(order =>
    order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.customerEmail.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleViewOrder = (order: Order) => {
    setSelectedOrder(order);
    setDetailDialogOpen(true);
  };

  const handleUpdateOrderStatus = (orderId: number, newStatus: Order['status']) => {
    setOrders(orders.map(order =>
      order.id === orderId ? { ...order, status: newStatus } : order
    ));
  };

  const getStatusChip = (status: Order['status']) => {
    const statusConfig = {
      PENDING: { label: 'Pending', color: '#FF7F2F', bg: '#FF7F2F20' },
      CONFIRMED: { label: 'Confirmed', color: '#35C5F0', bg: '#35C5F020' },
      SHIPPED: { label: 'Shipped', color: '#9C27B0', bg: '#9C27B020' },
      DELIVERED: { label: 'Delivered', color: '#4CAF50', bg: '#4CAF5020' },
      CANCELLED: { label: 'Cancelled', color: '#F44336', bg: '#F4433620' },
    };

    const config = statusConfig[status];
    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          backgroundColor: config.bg,
          color: config.color,
          fontWeight: 600,
        }}
      />
    );
  };

  const getPaymentStatusChip = (status: Order['paymentStatus']) => {
    const statusConfig = {
      PENDING: { label: 'Pending', color: '#FF7F2F', bg: '#FF7F2F20' },
      PAID: { label: 'Paid', color: '#4CAF50', bg: '#4CAF5020' },
      FAILED: { label: 'Failed', color: '#F44336', bg: '#F4433620' },
      REFUNDED: { label: 'Refunded', color: '#9E9E9E', bg: '#9E9E9E20' },
    };

    const config = statusConfig[status];
    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          backgroundColor: config.bg,
          color: config.color,
          fontWeight: 600,
        }}
      />
    );
  };

  const formatCurrency = (amount: number) => {
    return '₩' + new Intl.NumberFormat('ko-KR').format(amount);
  };

  if (loading) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={60} sx={{ color: '#35C5F0' }} />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: '#333',
            mb: 1,
          }}
        >
          Order Management
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Manage customer orders and update delivery status
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Status Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{ borderBottom: '1px solid #e0e0e0' }}
        >
          <Tab label={`All (${orders.length})`} />
          <Tab label={`Pending (${getOrdersByStatus('PENDING').length})`} />
          <Tab label={`Shipped (${getOrdersByStatus('SHIPPED').length})`} />
          <Tab label={`Delivered (${getOrdersByStatus('DELIVERED').length})`} />
        </Tabs>
      </Card>

      {/* Search and Filter */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', flex: 1 }}>
          <TextField
            placeholder="Search by order number, customer name..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#666' }} />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: '100%', sm: 300 } }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Orders Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Order Number</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Customer Info</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Order Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Payment Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Order Amount</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Order Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOrders.map((order) => (
                  <TableRow
                    key={order.id}
                    sx={{
                      '&:hover': { backgroundColor: '#f8f9fa' },
                      transition: 'background-color 0.2s ease',
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {order.orderNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {order.customerName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {order.customerEmail}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{getStatusChip(order.status)}</TableCell>
                    <TableCell>{getPaymentStatusChip(order.paymentStatus)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatCurrency(order.totalAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell>{order.orderDate}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <IconButton
                          size="small"
                          onClick={() => handleViewOrder(order)}
                          sx={{ color: '#35C5F0' }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        {order.status === 'PENDING' && (
                          <IconButton
                            size="small"
                            onClick={() => handleUpdateOrderStatus(order.id, 'CONFIRMED')}
                            sx={{ color: '#4CAF50' }}
                          >
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        )}
                        {order.status === 'CONFIRMED' && (
                          <IconButton
                            size="small"
                            onClick={() => handleUpdateOrderStatus(order.id, 'SHIPPED')}
                            sx={{ color: '#9C27B0' }}
                          >
                            <LocalShippingIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Order Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Order Details</DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Order Information
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Order Number
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedOrder.orderNumber}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Order Date
                    </Typography>
                    <Typography variant="body1">
                      {selectedOrder.orderDate}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Order Status
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {getStatusChip(selectedOrder.status)}
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Payment Status
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {getPaymentStatusChip(selectedOrder.paymentStatus)}
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Divider />

              <Box sx={{ my: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Customer Information
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Customer Name
                    </Typography>
                    <Typography variant="body1">
                      {selectedOrder.customerName}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Email
                    </Typography>
                    <Typography variant="body1">
                      {selectedOrder.customerEmail}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    Shipping Address
                  </Typography>
                  <Typography variant="body1">
                    {selectedOrder.shippingAddress}
                  </Typography>
                </Box>
              </Box>

              <Divider />

              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Order Items
                </Typography>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Product Name</TableCell>
                      <TableCell align="center">Quantity</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Subtotal</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedOrder.items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.productName}</TableCell>
                        <TableCell align="center">{item.quantity}</TableCell>
                        <TableCell align="right">{formatCurrency(item.price)}</TableCell>
                        <TableCell align="right">
                          {formatCurrency(item.price * item.quantity)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={3} sx={{ fontWeight: 600 }}>
                        총 주문금액
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                        {formatCurrency(selectedOrder.totalAmount)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>
            Close
          </Button>
          {selectedOrder && selectedOrder.status === 'PENDING' && (
            <Button
              variant="contained"
              sx={{
                backgroundColor: '#4CAF50',
                '&:hover': { backgroundColor: '#388E3C' },
              }}
              onClick={() => {
                handleUpdateOrderStatus(selectedOrder.id, 'CONFIRMED');
                setDetailDialogOpen(false);
              }}
            >
              Confirm Order
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminOrders;